"use client";

import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Loader2, ChevronDown, MessageSquare } from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

import { useGetOrdersQuery } from "@/features/api/orderApi";

const MyOrdersPage = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const [openOrderId, setOpenOrderId] = useState(null);

  /* ------------------------------------------
     Redirect if not logged in
  ------------------------------------------ */
  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
  }, [user, navigate]);

  /* ------------------------------------------
     Fetch Orders
  ------------------------------------------ */
  const {
    data,
    isLoading,
    isError,
    refetch,
  } = useGetOrdersQuery(
    { page: 1, limit: 100 },
    { skip: !user }
  );

  /* ------------------------------------------
     Normalize Orders Response
  ------------------------------------------ */
  const orders = useMemo(() => {
    if (!data) return [];
    return data.transactions || data.orders || data.data || [];
  }, [data]);

  /* ------------------------------------------
     Filter Current User Orders (Safety Layer)
  ------------------------------------------ */
  const myOrders = useMemo(() => {
    if (!user) return [];

    return orders.filter(
      (order) =>
        order?.email === user?.email ||
        order?.userId === user?._id ||
        order?.userId === user?.id
    );
  }, [orders, user]);

  const toggleOrder = (id) => {
    setOpenOrderId((prev) => (prev === id ? null : id));
  };

  /* ------------------------------------------
     Status Badge Styling
  ------------------------------------------ */
  const getBadgeVariant = (status) => {
    if (!status) return "secondary";

    const normalized = status.toUpperCase();

    if (normalized === "SUCCESS") return "success";
    if (normalized === "PENDING") return "secondary";
    return "destructive";
  };

  /* ------------------------------------------
     Loading State
  ------------------------------------------ */
  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  /* ------------------------------------------
     Error State
  ------------------------------------------ */
  if (isError) {
    return (
      <div className="text-center py-20 space-y-4">
        <p className="text-red-500 font-medium">
          Failed to load your orders.
        </p>
        <Button onClick={refetch}>Retry</Button>
      </div>
    );
  }

  /* ------------------------------------------
     UI
  ------------------------------------------ */
  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <h1 className="text-3xl font-bold">My Orders</h1>

      {myOrders.length === 0 ? (
        <p className="text-gray-500">
          You have not placed any orders yet.
        </p>
      ) : (
        <div className="space-y-4">
          {myOrders.map((order) => {
            const isOpen = openOrderId === order._id;
            const status = order.status?.toUpperCase();

            return (
              <Card key={order._id}>
                {/* ---------------- HEADER ---------------- */}
                <CardHeader
                  className="cursor-pointer"
                  onClick={() => toggleOrder(order._id)}
                >
                  <CardTitle className="flex justify-between items-center">
                    <div>
                      <p className="font-medium">
                        {order.courseName ||
                          order.productName ||
                          "Purchase"}
                      </p>
                      <p className="text-sm text-gray-500">
                        {new Date(
                          order.createdAt
                        ).toLocaleDateString()}
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <Badge variant={getBadgeVariant(status)}>
                        {status}
                      </Badge>
                      <ChevronDown
                        className={`h-5 w-5 transition-transform ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                  </CardTitle>
                </CardHeader>

                {/* ---------------- DETAILS ---------------- */}
                {isOpen && (
                  <CardContent className="space-y-4 text-sm">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <p>
                        <strong>Amount:</strong> ₹
                        {Number(order.amount || 0).toFixed(2)}
                      </p>

                      <p>
                        <strong>Transaction ID:</strong>{" "}
                        {order.transactionId || "N/A"}
                      </p>

                      <p>
                        <strong>order Method:</strong>{" "}
                        {order.method || "Online"}
                      </p>

                      <p>
                        <strong>Order Date:</strong>{" "}
                        {new Date(
                          order.createdAt
                        ).toLocaleString()}
                      </p>

                      {order.courseId && (
                        <p>
                          <strong>Course ID:</strong>{" "}
                          {order.courseId}
                        </p>
                      )}
                    </div>

                    {/* ---------------- ACTIONS ---------------- */}
                    <div className="flex flex-wrap gap-3 pt-2">
                      {status === "SUCCESS" && (
                        <>
                          <Button
                            size="sm"
                            onClick={() =>
                              navigate("/dashboard/mycourses")
                            }
                          >
                            View Course
                          </Button>
                  </>
                      )}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyOrdersPage;